import React, { memo, useCallback, useMemo } from "react"
import { css } from "@emotion/react"
import {
	CustomerPartQueryParams,
	FULL_PAGE_SIZE,
	initialCustomerPartQueryParams,
	useCategories,
	useOnlinePartBrands,
	usePartManufacturers,
	usePartSystems,
} from "@ncs/ncs-api"
import { arrayWrap, SubTypeIncluded, toggleArrayItem } from "@ncs/ts-utils"
import { AnimatedEntrance, Chip, useUrlState } from "@ncs/web-legos"
import { PartSearchResultsUrlState } from "../PartSearchResults"

export const ActiveFiltersEcomm: React.FC = memo(() => {
	const [manufacturers] = usePartManufacturers()
	const [systems] = usePartSystems()
	const [categories] = useCategories()
	const { data: brands } = useOnlinePartBrands({
		params: {
			pageSize: FULL_PAGE_SIZE,
			isActive: true,
		},
		manualPagination: true,
	})
	const [partQueryParams, { setUrlState }] = useUrlState<PartSearchResultsUrlState>(
		initialCustomerPartQueryParams
	)

	const deleteFeaturedFilter = useCallback(() => {
		setUrlState((prev) => ({
			...prev,
			featured: null,
			page: 1,
		}))
	}, [setUrlState])

	const deletePriceFilter = useCallback(() => {
		setUrlState((prev) => ({
			...prev,
			price_Gte: null,
			price_Lt: null,
			page: 1,
		}))
	}, [setUrlState])

	const deleteArrayFilter = useCallback(
		(idToRemove: string, type: (typeof arrayFiltersToShow)[number]) => {
			setUrlState((prev: any) => ({
				...prev,
				[type]: toggleArrayItem(
					idToRemove,
					arrayWrap(partQueryParams[type]).map((itemId) => String(itemId))
				),
				page: 1,
			}))
		},
		[setUrlState, partQueryParams]
	)

	// These are the query params that are arrays of things, like arrays of systems to include.
	const arrayFiltersToShow: (keyof SubTypeIncluded<CustomerPartQueryParams, string[]>)[] =
		useMemo(() => ["manufacturers", "systems", "categories", "brands"], [])

	const priceChipLabel = useMemo(() => {
		const min = partQueryParams.price_Gte
		const max = partQueryParams.price_Lt

		if (!min && !max) return null

		const formatPrice = (price: string) => {
			const num = parseFloat(price)
			return `$${num.toLocaleString(undefined, {
				minimumFractionDigits: num % 1 === 0 ? 0 : 2,
				maximumFractionDigits: 2,
			})}`
		}

		if (min && max) {
			return `${formatPrice(min)} - ${formatPrice(max)}`
		} else if (min) {
			return `${formatPrice(min)}+`
		} else if (max) {
			return `Up to ${formatPrice(max)}`
		}

		return null
	}, [partQueryParams.price_Gte, partQueryParams.price_Lt])

	const getArrayChipLabel = useCallback(
		(id: string, filterType: (typeof arrayFiltersToShow)[number]) => {
			switch (filterType) {
				case "manufacturers": {
					return `Brand: ${
						(manufacturers ?? []).find((b) => String(b.id) === id)?.name ?? ""
					}`
				}
				case "categories": {
					return `Category: ${
						(categories ?? []).find((c) => String(c.id) === id)?.name ?? ""
					}`
				}
				case "systems": {
					return `System: ${
						(systems ?? []).find((s) => String(s.id) === id)?.modelName ?? ""
					}`
				}
				case "brands": {
					return `Brand: ${(brands ?? []).find((b) => String(b.id) === id)?.name ?? ""}`
				}
				default:
					return "Unknown filter type"
			}
		},
		[manufacturers, categories, systems, brands]
	)

	// The array filters are a bit trickier, so we'll do them up here by themselves.
	const activeArrayFilters = useMemo(() => {
		return arrayFiltersToShow.map((filter) =>
			arrayWrap(partQueryParams[filter]).map((id) => (
				<li key={`${filter}${id}`}>
					<AnimatedEntrance show>
						<Chip
							label={getArrayChipLabel(id.toString(), filter) || ""}
							onDelete={() => deleteArrayFilter(id.toString(), filter)}
							title="Remove filter"
						/>
					</AnimatedEntrance>
				</li>
			))
		)
	}, [partQueryParams, arrayFiltersToShow, getArrayChipLabel, deleteArrayFilter])

	return (
		<ul css={chipListStyle}>
			{partQueryParams.featured && (
				<li>
					<AnimatedEntrance show>
						<Chip label="Trending" onDelete={deleteFeaturedFilter} />
					</AnimatedEntrance>
				</li>
			)}
			{(partQueryParams.price_Gte || partQueryParams.price_Lt) && (
				<li>
					<AnimatedEntrance show>
						<Chip
							label={`Price: ${priceChipLabel}`}
							onDelete={deletePriceFilter}
							title="Remove filter"
						/>
					</AnimatedEntrance>
				</li>
			)}
			{activeArrayFilters}
		</ul>
	)
})

const chipListStyle = css`
	display: flex;
	flex-wrap: wrap;
	li {
		margin-right: 0.5rem;
		margin-bottom: 0.5rem;
	}
`
